import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Container,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Button,
    Box,
    useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getActivityById } from '../services/api/activity';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTheme } from '../context/ThemeContext';
import { showAlert } from '../components/service/AlertService';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const Activity = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const { actualTheme } = useTheme();
    const [activityData, setActivityData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showComparator, setShowComparator] = useState(false);
    const [mainLaps, setMainLaps] = useState([]);
    const [redLaps, setRedLaps] = useState([]);
    const [blueLaps, setBlueLaps] = useState([]);
    const [originalLaps, setOriginalLaps] = useState([]);

    useEffect(() => {
        const fetchActivity = async () => {
            try {
                const response = await getActivityById(id);
                if (!response.data) {
                    showAlert(response.error.code || 'ACTIVITY_NOT_FOUND');
                } else {
                    const lapsWithIndex = (response.data.laps || []).map((lap, index) => ({
                        ...lap,
                        originalIndex: index,
                        rawLapTime: lap.lapTime,
                        lapTime: formatLapTime(lap.lapTime),
                    }));
                    setActivityData(response.data);
                    setMainLaps(lapsWithIndex);
                    setOriginalLaps(lapsWithIndex);
                }
            } catch (err) {
                showAlert('DEFAULT');
                setLoading(false);
            } finally {
                setLoading(false);
            }
        };

        fetchActivity();
    }, [id, t]);

    const format2Metric = (value) => (value ? Number(value).toFixed(2) : 'N/A');
    const format4Metric = (value) => (value ? Number(value).toFixed(4) : 'N/A');

    const moveLap = (index, fromTable, toTable) => {
        let lap;
        if (fromTable === 'main') {
            lap = mainLaps[index];
            setMainLaps(mainLaps.filter((_, i) => i !== index));
        } else if (fromTable === 'red') {
            lap = redLaps[index];
            setRedLaps(redLaps.filter((_, i) => i !== index));
        } else if (fromTable === 'blue') {
            lap = blueLaps[index];
            setBlueLaps(blueLaps.filter((_, i) => i !== index));
        }

        if (toTable === 'red') {
            setRedLaps((prev) => [
                ...prev.filter((l) => l.originalIndex !== lap.originalIndex),
                lap,
            ]);
        } else if (toTable === 'blue') {
            setBlueLaps((prev) => [
                ...prev.filter((l) => l.originalIndex !== lap.originalIndex),
                lap,
            ]);
        } else if (toTable === 'main') {
            setMainLaps((prev) => {
                let newMainLaps = prev.filter((l) => l.originalIndex !== lap.originalIndex);
                const originalIndex = lap.originalIndex;
                let insertIndex = 0;
                for (let i = 0; i < newMainLaps.length; i++) {
                    if (newMainLaps[i].originalIndex < originalIndex) {
                        insertIndex = i + 1;
                    } else {
                        break;
                    }
                }
                newMainLaps.splice(insertIndex, 0, lap);
                return newMainLaps;
            });
        }
    };

    const resetTables = () => {
        setMainLaps([...originalLaps]);
        setRedLaps([]);
        setBlueLaps([]);
    };

    // Data for the main chart
    const mainChartData = {
        labels: mainLaps.map((lap) => `Lap ${lap.originalIndex + 1}`),
        datasets: [
            {
                label: t('activityDetails.laps.aerodynamicDragCoefficient'),
                data: mainLaps.map((lap) => Number(lap.aerodynamicDragCoefficient) || 0),
                borderColor: actualTheme === 'light' ? 'rgba(33, 150, 243, 1)' : 'rgba(33, 150, 243, 1)',
                backgroundColor: actualTheme === 'light' ? 'rgba(33, 150, 243, 0.2)' : 'rgba(33, 150, 243, 0.2)',
                fill: true,
                tension: 0.4,
                pointRadius: 5,
                pointHoverRadius: 7,
            },
        ],
    };

    // Data for the chart of the red table
    const redChartData = {
        labels: redLaps.map((_, index) => `Lap ${index + 1}`),
        datasets: [
            {
                label: t('activityDetails.laps.aerodynamicDragCoefficient'),
                data: redLaps.map((lap) => Number(lap.aerodynamicDragCoefficient) || 0),
                borderColor: actualTheme === 'light' ? 'rgba(211, 47, 47, 1)' : 'rgba(255, 0, 0, 1)',
                backgroundColor: actualTheme === 'light' ? 'rgba(211, 47, 47, 0.2)' : 'rgba(255, 0, 0, 0.2)',
                fill: true,
                tension: 0.4,
            },
        ],
    };

    // Data for the chart of the blue table
    const blueChartData = {
        labels: blueLaps.map((_, index) => `Lap ${index + 1}`),
        datasets: [
            {
                label: t('activityDetails.laps.aerodynamicDragCoefficient'),
                data: blueLaps.map((lap) => Number(lap.aerodynamicDragCoefficient) || 0),
                borderColor: actualTheme === 'light' ? 'rgba(25, 118, 210, 1)' : 'rgba(0, 0, 255, 1)',
                backgroundColor: actualTheme === 'light' ? 'rgba(25, 118, 210, 0.2)' : 'rgba(0, 0, 255, 0.2)',
                fill: true,
                tension: 0.4,
            },
        ],
    };

    // Data for the combined chart
    const combinedChartData = {
        labels: Array.from({ length: Math.max(redLaps.length, blueLaps.length) }, (_, i) => `Lap ${i + 1}`),
        datasets: [
            {
                label: `${t('activityDetails.redTable')} ${t('activityDetails.laps.aerodynamicDragCoefficient')}`,
                data: redLaps.map((lap) => Number(lap.aerodynamicDragCoefficient) || 0),
                borderColor: actualTheme === 'light' ? 'rgba(211, 47, 47, 1)' : 'rgba(255, 0, 0, 1)',
                backgroundColor: actualTheme === 'light' ? 'rgba(211, 47, 47, 0.2)' : 'rgba(255, 0, 0, 0.2)',
                fill: false,
                tension: 0.4,
            },
            {
                label: `${t('activityDetails.blueTable')} ${t('activityDetails.laps.aerodynamicDragCoefficient')}`,
                data: blueLaps.map((lap) => Number(lap.aerodynamicDragCoefficient) || 0),
                borderColor: actualTheme === 'light' ? 'rgba(25, 118, 210, 1)' : 'rgba(0, 0, 255, 1)',
                backgroundColor: actualTheme === 'light' ? 'rgba(25, 118, 210, 0.2)' : 'rgba(0, 0, 255, 0.2)',
                fill: false,
                tension: 0.4,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    color: actualTheme === 'light' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.87)',
                },
            },
            title: {
                display: true,
                text: t('activityDetails.laps.aerodynamicDragCoefficient'),
                color: actualTheme === 'light' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.87)',
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: t('activityDetails.laps.lapNumber'),
                    color: actualTheme === 'light' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.87)',
                },
                ticks: {
                    color: actualTheme === 'light' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.87)',
                },
            },
            y: {
                title: {
                    display: true,
                    text: t('activityDetails.laps.aerodynamicDragCoefficient'),
                    color: actualTheme === 'light' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.87)',
                },
                ticks: {
                    color: actualTheme === 'light' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.87)',
                    beginAtZero: true,
                    stepSize: 0.1,
                },
                suggestedMin: 0,
                suggestedMax: Math.max(...mainLaps.map(lap => Number(lap.aerodynamicDragCoefficient) || 0)) * 1.2 || 1, // Máximo dinámico con margen
            },
        },
    };

    return (
        <DndProvider backend={HTML5Backend}>
            <Container maxWidth="lg" sx={{ mt: 12, mb: 4 }}>
                <Typography
                    variant="h4"
                    component="h1"
                    sx={{
                        mb: 4,
                        fontWeight: 300,
                        letterSpacing: '0.1em',
                        color: 'text.primary',
                    }}
                >
                    {t('activityDetails.title')} {activityData?.activityName || ''}
                </Typography>

                {loading && <CircularProgress />}

                {activityData && (
                    <Paper
                        sx={{
                            p: { xs: 2, sm: 4 },
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                mb: 2,
                                color: 'text.primary',
                                fontWeight: 300,
                                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                maxWidth: '100%',
                            }}
                        >
                            {t('activityDetails.description')}: {activityData.activityDescription || ''}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ mb: 3, color: 'text.secondary' }}
                        >
                            {t('activityDetails.createdAt')}: {new Date(activityData.createdAt).toLocaleString()}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ mb: 3, color: 'text.secondary' }}
                        >
                            {t('activityDetails.airDensity')}: {format2Metric(activityData.airDensity)} kg/m³ |{' '}
                            {t('activityDetails.cyclistWeight')}: {format2Metric(activityData.cyclistWeight)} kg |{' '}
                            {t('activityDetails.luggageWeight')}: {format2Metric(activityData.luggageWeight)} kg |{' '}
                            {t('activityDetails.totalWeight')}: {format2Metric(activityData.totalWeight)} N |{' '}
                            {t('activityDetails.rollingResistanceCoefficient')}: {format4Metric(activityData.rollingResistanceCoefficient)}
                        </Typography>

                        <Box sx={{ mb: 3 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setShowComparator(!showComparator)}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 300,
                                    '&:hover': {
                                        backgroundColor: (theme) =>
                                            theme.palette.mode === 'light'
                                                ? 'primary.light'
                                                : 'primary.dark',
                                    },
                                }}
                            >
                                {t('activityDetails.comparator')}
                            </Button>
                            {showComparator && (
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={resetTables}
                                    sx={{
                                        ml: 2,
                                        textTransform: 'none',
                                        fontWeight: 300,
                                        '&:hover': {
                                            backgroundColor: (theme) =>
                                                theme.palette.mode === 'light'
                                                    ? 'error.light'
                                                    : 'error.dark',
                                        },
                                    }}
                                >
                                    {t('activityDetails.reset')}
                                </Button>
                            )}
                        </Box>

                        {/* Main Chart */}
                        {mainLaps.length > 0 && (
                            <Box sx={{ mt: 4, maxWidth: '100%', overflowX: 'auto', minHeight: '300px' }}>
                                <Line data={mainChartData} options={chartOptions} />
                            </Box>
                        )}

                        <DroppableTable
                            laps={mainLaps}
                            title={t('activityDetails.mainTable')}
                            color="main"
                            tableType="main"
                            moveLap={moveLap}
                        />

                        {showComparator && (
                            <>
                                <Box sx={{ mt: 4 }}>
                                    <DroppableTable
                                        laps={redLaps}
                                        title={t('activityDetails.redTable')}
                                        color="red"
                                        tableType="red"
                                        moveLap={moveLap}
                                    />
                                </Box>
                                <Box sx={{ mt: 4 }}>
                                    <DroppableTable
                                        laps={blueLaps}
                                        title={t('activityDetails.blueTable')}
                                        color="blue"
                                        tableType="blue"
                                        moveLap={moveLap}
                                    />
                                </Box>

                                {/* Gráficos en la misma línea */}
                                {(redLaps.length > 0 || blueLaps.length > 0) && (
                                    <Box sx={{ mt: 4, display: 'flex', gap: 4, maxWidth: '100%', overflowX: 'auto', minHeight: '300px' }}>
                                        {redLaps.length > 0 && (
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Line data={redChartData} options={chartOptions} />
                                            </Box>
                                        )}
                                        {blueLaps.length > 0 && (
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Line data={blueChartData} options={chartOptions} />
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </>
                        )}

                        {/* Combinated Chart */}
                        {showComparator && (redLaps.length > 0 || blueLaps.length > 0) && (
                            <Box sx={{ mt: 4, maxWidth: '100%', overflowX: 'auto', minHeight: '400px' }}>
                                <Line data={combinedChartData} options={chartOptions} />
                            </Box>
                        )}
                    </Paper>
                )}
            </Container>
        </DndProvider>
    );
};

const ItemTypes = {
    LAP: 'lap',
};

const formatLapTime = (lapTime) => {
    if (!lapTime && lapTime !== 0) return 'N/A';
    if (typeof lapTime === 'number') {
        const minutes = Math.floor(lapTime / 60);
        const seconds = (lapTime % 60).toFixed(1);
        return `${minutes}:${seconds.padStart(4, '0')}`;
    }
    const parts = lapTime.split(':');
    return parts.length === 4 ? `${parts[2]}:${parts[3]}` : lapTime;
};

const DraggableTableRow = ({ lap, index, moveLap, tableType }) => {
    const { actualTheme } = useTheme();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const format2Metric = (value) => (value ? Number(value).toFixed(2) : 'N/A');
    const format4Metric = (value) => (value ? Number(value).toFixed(4) : 'N/A');

    const [{ isDragging }, drag] = useDrag({
        type: ItemTypes.LAP,
        item: { index, tableType },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    return (
        <TableRow
            ref={drag}
            sx={{
                '&:hover': {
                    backgroundColor: (theme) =>
                        theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.04)'
                            : 'rgba(255, 255, 255, 0.05)',
                },
                opacity: isDragging ? 0.5 : 1,
                height: '60px', // Aumentar la altura de las filas
                '& td': {
                    padding: '12px', // Aumentar el padding para que no se vea tan plana
                },
            }}
        >
            <TableCell sx={{ color: 'text.primary' }}>{lap.originalIndex + 1}</TableCell>
            {!isMobile && (
                <>
                    <TableCell sx={{ color: 'text.primary' }}>{lap.lapTime}</TableCell>
                    <TableCell sx={{ color: 'text.primary' }}>{lap.distanceMeters}</TableCell>
                    <TableCell sx={{ color: 'text.primary' }}>{format2Metric(lap.velocityKmH)}</TableCell>
                    <TableCell sx={{ color: 'text.primary' }}>{format2Metric(lap.cadence)}</TableCell>
                    <TableCell sx={{ color: 'text.primary' }}>{format2Metric(lap.power)}</TableCell>
                </>
            )}
            <TableCell sx={{ color: 'text.primary' }}>{format2Metric(lap.rollingResistance)}</TableCell>
            <TableCell sx={{ color: 'text.primary' }}>{format4Metric(lap.aerodynamicDragCoefficient)}</TableCell>
        </TableRow>
    );
};

const DroppableTable = ({ laps, title, color, tableType, moveLap }) => {
    const { t } = useTranslation();
    const { actualTheme } = useTheme();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const format2Metric = (value) => (value ? Number(value).toFixed(2) : 'N/A');
    const format4Metric = (value) => (value ? Number(value).toFixed(4) : 'N/A');

    const [{ isOver }, drop] = useDrop({
        accept: ItemTypes.LAP,
        canDrop: (item) => item.tableType !== tableType,
        drop: (item) => moveLap(item.index, item.tableType, tableType),
        collect: (monitor) => ({
            isOver: monitor.isOver(),
        }),
    });

    const convertTimeToMs = (time) => {
        const [hours, minutes, seconds, milliseconds] = time.split(':').map(Number);
        return (
            (hours * 60 * 60 * 1000) +
            (minutes * 60 * 1000) +
            (seconds * 1000) +
            milliseconds
        );
    };

    const convertMsToTime = (ms) => {
        const seconds = Math.floor(ms / 1000);
        const milliseconds = Math.floor(ms % 1000);
        return `${seconds}:${milliseconds.toString().padStart(3, '0')}`;
    };

    const calculateAverages = () => {
        if (laps.length === 0) return null;
        const fields = [
            'distanceMeters',
            'velocityKmH',
            'cadence',
            'power',
            'rollingResistance',
            'aerodynamicDragCoefficient',
        ];
        const averages = {};
        fields.forEach((field) => {
            const sum = laps.reduce((acc, lap) => acc + (Number(lap[field]) || 0), 0);
            averages[field] = sum / laps.length;
        });
        const lapTimeSum = laps.reduce((acc, lap) => acc + (convertTimeToMs(lap.rawLapTime) || 0), 0);
        averages.lapTime = convertMsToTime(lapTimeSum / laps.length);
        return averages;
    };

    const averages = calculateAverages();

    const tableColors = {
        main: actualTheme === 'light' ? '33, 150, 243' : '33, 150, 243',
        red: actualTheme === 'light' ? '211, 47, 47' : '255, 0, 0',
        blue: actualTheme === 'light' ? '25, 118, 210' : '0, 0, 255',
    };

    return (
        <Box sx={{ mt: title ? 4 : 0 }}>
            {title && (
                <Typography
                    variant="h6"
                    sx={{
                        mb: 2,
                        color: 'text.primary',
                        fontWeight: 300,
                        textAlign: 'center',
                    }}
                >
                    {title}
                </Typography>
            )}
            <TableContainer
                ref={drop}
                sx={{
                    maxWidth: '100%',
                    overflowX: 'auto',
                    background: (theme) =>
                        theme.palette.mode === 'light'
                            ? `rgba(${tableColors[tableType]}, 0.1)`
                            : `rgba(${tableColors[tableType]}, 0.05)`,
                    border: (theme) =>
                        theme.palette.mode === 'light'
                            ? `1px solid rgba(${tableColors[tableType]}, 0.3)`
                            : `1px solid rgba(${tableColors[tableType]}, 0.3)`,
                    borderRadius: '2px',
                    opacity: isOver ? 0.8 : 1,
                    '&::-webkit-scrollbar': { height: '8px' },
                    '&::-webkit-scrollbar-track': {
                        background: (theme) =>
                            theme.palette.mode === 'light'
                                ? 'rgba(0, 0, 0, 0.05)'
                                : 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: (theme) =>
                            theme.palette.mode === 'light'
                                ? `rgba(${tableColors[tableType]}, 0.5)`
                                : `rgba(${tableColors[tableType]}, 0.5)`,
                        borderRadius: '4px',
                        '&:hover': {
                            background: (theme) =>
                                theme.palette.mode === 'light'
                                    ? `rgba(${tableColors[tableType]}, 0.7)`
                                    : `rgba(${tableColors[tableType]}, 0.7)`,
                        },
                    },
                    scrollbarWidth: 'thin',
                    scrollbarColor: (theme) =>
                        theme.palette.mode === 'light'
                            ? `rgba(${tableColors[tableType]}, 0.5) rgba(0, 0, 0, 0.05)`
                            : `rgba(${tableColors[tableType]}, 0.5) rgba(255, 255, 255, 0.05)`,
                }}
            >
                <Table
                    sx={{
                        minHeight: '200px', // Aumentar la altura mínima de la tabla
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ color: 'text.primary', fontWeight: 300, padding: '12px' }}>
                                {t('activityDetails.laps.lapNumber')}
                            </TableCell>
                            {!isMobile && (
                                <>
                                    <TableCell sx={{ color: 'text.primary', fontWeight: 300, padding: '12px' }}>
                                        {t('activityDetails.laps.lapTime')}
                                    </TableCell>
                                    <TableCell sx={{ color: 'text.primary', fontWeight: 300, padding: '12px' }}>
                                        {t('activityDetails.laps.distanceMeters')}
                                    </TableCell>
                                    <TableCell sx={{ color: 'text.primary', fontWeight: 300, padding: '12px' }}>
                                        {t('activityDetails.laps.velocityKmH')}
                                    </TableCell>
                                    <TableCell sx={{ color: 'text.primary', fontWeight: 300, padding: '12px' }}>
                                        {t('activityDetails.laps.cadence')}
                                    </TableCell>
                                    <TableCell sx={{ color: 'text.primary', fontWeight: 300, padding: '12px' }}>
                                        {t('activityDetails.laps.power')}
                                    </TableCell>
                                </>
                            )}
                            <TableCell sx={{ color: 'text.primary', fontWeight: 300, padding: '12px' }}>
                                {t('activityDetails.laps.rollingResistance')}
                            </TableCell>
                            <TableCell sx={{ color: 'text.primary', fontWeight: 300, padding: '12px' }}>
                                {t('activityDetails.laps.aerodynamicDragCoefficient')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {laps.map((lap, index) => (
                            <DraggableTableRow
                                key={lap.originalIndex}
                                lap={lap}
                                index={index}
                                moveLap={moveLap}
                                tableType={tableType}
                            />
                        ))}
                        {averages && tableType !== 'main' && (
                            <TableRow
                                sx={{
                                    backgroundColor: (theme) =>
                                        theme.palette.mode === 'light'
                                            ? `rgba(${tableColors[tableType]}, 0.15)`
                                            : `rgba(${tableColors[tableType]}, 0.1)`,
                                }}
                            >
                                <TableCell sx={{ color: 'text.primary', fontWeight: 500, padding: '12px' }}>
                                    {t('activityDetails.averages')}
                                </TableCell>
                                {!isMobile && (
                                    <>
                                        <TableCell sx={{ color: 'text.primary', fontWeight: 500, padding: '12px' }}>
                                            {formatLapTime(averages.lapTime)}
                                        </TableCell>
                                        <TableCell sx={{ color: 'text.primary', fontWeight: 500, padding: '12px' }}>
                                            {format2Metric(averages.distanceMeters)}
                                        </TableCell>
                                        <TableCell sx={{ color: 'text.primary', fontWeight: 500, padding: '12px' }}>
                                            {format2Metric(averages.velocityKmH)}
                                        </TableCell>
                                        <TableCell sx={{ color: 'text.primary', fontWeight: 500, padding: '12px' }}>
                                            {format2Metric(averages.cadence)}
                                        </TableCell>
                                        <TableCell sx={{ color: 'text.primary', fontWeight: 500, padding: '12px' }}>
                                            {format2Metric(averages.power)}
                                        </TableCell>
                                    </>
                                )}
                                <TableCell sx={{ color: 'text.primary', fontWeight: 500, padding: '12px' }}>
                                    {format2Metric(averages.rollingResistance)}
                                </TableCell>
                                <TableCell sx={{ color: 'text.primary', fontWeight: 500, padding: '12px' }}>
                                    {format4Metric(averages.aerodynamicDragCoefficient)}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default Activity;